/**
 * Schema & data formatting
 */
import clone from 'clone';
import { AJV_EXTENSION_PROPERTY_PATH, AJV_EXTENSION_REF_SCHEMA } from '../../globals';
import {
    ISchemaAnyOf,
    ISchemaArray,
    ISchemaCoreType,
    ISchemaObject,
    ISchemaObjectProperties,
    ISchemaOneOf,
    ISchemaRecord,
    ISchemaRef,
    ISchemaRefFormatted,
    ISchemaTuple,
    ISchemaType,
} from '../properties';
import {
    ISchemaDate,
    ISchemaString,
    SchemaStringFormat,
    SchemaTypeName,
} from '../properties/primitives';
import { dateToString } from './common';

/**
 * Reformat schema for AJV compatibility
 * - The schemata assembled by the package's `Type` export are designed for ease-of-use,
 *   and are NOT AJV/JSON Schema-compatible:
 *   - This package supports Date objects for date formatting, where AJV/JSON Schema require strings
 *   - This package supports regex patterns for string patterns, where AJV/JSON Schema require strings
 * - Here, we convert from package format to AJV/JSON Schema format
 */
export function formatSchemaForAJV<T extends ISchemaType>({
    schema: input_schema,
    ref_path,
}: {
    schema: T;
    ref_path: string;
}): T {
    const schema = clone(input_schema);

    /**
     * $ref: Shift schema properties – apart from $ref itself – into [AJV_EXTENSION_REF_SCHEMA]
     */
    const ref_schema = schema as ISchemaRef;
    if (ref_schema.$ref) {
        /**
         * We will potentially scan over a property schema multiple times, but only want to shift
         * non-$ref fields to [AJV_EXTENSION_REF_SCHEMA] once!
         */
        // @ts-expect-error: Pseudo-field
        if (ref_schema[AJV_EXTENSION_REF_SCHEMA]) {
            return (ref_schema as unknown) as T;
        }

        //  Prune $ref from extension schema
        const {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            $ref,
            ...extension_schema
        } = ref_schema;

        const response: ISchemaRefFormatted = {
            $ref: ref_schema.$ref,
            [AJV_EXTENSION_REF_SCHEMA]: extension_schema,
            [AJV_EXTENSION_PROPERTY_PATH]: ref_path,
        };

        return response as T;
    }

    /**
     * Core type (primitive + nested)
     */
    const core_schema = schema as Extract<T, ISchemaCoreType>;
    if (core_schema.type) {
        /**
         * Note that formatting conditionality is handled in the functions themselves, and not here,
         *  hence why we can nest all format functions here
         * - e.g. the array-type function checks whether input schema is type:array,
         *        the object-type function checks whether input schema is type:object,
         *        etc.
         *
         * Type-formatting function requirements:
         * - Nested-type format functions must have:
         *   - parameter format `{ ref_path, schema }`
         *   - & return format `{ ref_path, schema }`
         * - while primitive-type format functions must have:
         *   - parameter format `schema`
         *   - & return format `schema`
         */
        return formatArrayTypeForAJV({
            ref_path,
            schema: formatObjectTypeForAJV({
                ref_path,
                schema: formatStringTypeForAJV(core_schema),
            }),
        });
    }

    /**
     * Union/logical types
     */
    const any_of_schema = schema as ISchemaAnyOf<Extract<T, ISchemaCoreType>>;
    const one_of_schema = schema as ISchemaOneOf<Extract<T, ISchemaCoreType>>;

    if (any_of_schema.anyOf) {
        any_of_schema.anyOf = any_of_schema.anyOf.map((t, index) =>
            formatSchemaForAJV({
                schema: conditionallyAppendPropertyPath({
                    schema: t,
                    ref_path: `${ref_path}/anyOf/${index}`,
                }),
                ref_path: `${ref_path}/anyOf/${index}`,
            })
        );
    } else if (one_of_schema.oneOf) {
        one_of_schema.oneOf = one_of_schema.oneOf.map((t, index) =>
            formatSchemaForAJV({
                schema: conditionallyAppendPropertyPath({
                    schema: t,
                    ref_path: `${ref_path}/oneOf/${index}`,
                }),
                ref_path: `${ref_path}/oneOf/${index}`,
            })
        );
    }

    return schema;
}

/**
 * Format array/tuple schema for AJV compatibility
 * - Traverse tree down into nested schemata
 */
function formatArrayTypeForAJV<T extends ISchemaCoreType>({
    schema,
    ref_path,
}: {
    schema: T;
    ref_path: string;
}): T {
    //  array
    const array_schema = schema as ISchemaArray;
    if (array_schema.type === SchemaTypeName.array && array_schema.items) {
        array_schema.items = formatSchemaForAJV({
            schema: conditionallyAppendPropertyPath({
                schema: array_schema.items,
                ref_path: `${ref_path}/items`,
            }),
            ref_path: `${ref_path}/items`,
        });
    }

    //  tuple
    const tuple_schema = schema as ISchemaTuple;
    if (tuple_schema.type === SchemaTypeName.array && tuple_schema.prefixItems) {
        tuple_schema.prefixItems = tuple_schema.prefixItems.map((item, index) =>
            formatSchemaForAJV({
                schema: conditionallyAppendPropertyPath({
                    schema: item,
                    ref_path: `${ref_path}/prefixItems/${index}`,
                }),
                ref_path: `${ref_path}/prefixItems/${index}`,
            })
        );
    }

    return schema;
}

/**
 * Format object/record schema for AJV compatibility
 * - Traverse tree down into nested properties
 */
function formatObjectTypeForAJV<T extends ISchemaCoreType>({
    schema,
    ref_path,
}: {
    schema: T;
    ref_path: string;
}): T {
    //  object
    const object_schema = schema as ISchemaObject<any, string>;
    if (object_schema.type === SchemaTypeName.object && object_schema.properties) {
        object_schema.properties = formatObjectPropertiesForAJV({
            properties: object_schema.properties,
            ref_path_prefix: `${ref_path}/properties`,
        });
    }

    //  record
    const record_schema = schema as ISchemaRecord<any>;
    if (record_schema.type === SchemaTypeName.object && record_schema.patternProperties) {
        record_schema.patternProperties = formatObjectPropertiesForAJV({
            properties: record_schema.patternProperties,
            ref_path_prefix: `${ref_path}/patternProperties`,
        });
    }

    //  object/record
    if (object_schema.type === SchemaTypeName.object && object_schema.propertyNames) {
        object_schema.propertyNames = formatStringTypeForAJV(object_schema.propertyNames);
    }

    /**
     * @todo: If adding support for `additionalProperties: <schema>`, we'll need to format
     */

    return schema as T;
}

/**
 * Format Object property index for AJV compatibility
 */
export function formatObjectPropertiesForAJV<
    Properties extends ISchemaObjectProperties<Extract<keyof Properties, string>>
>({
    properties: input_properties,
    ref_path_prefix,
}: {
    properties: Properties;
    ref_path_prefix: string;
}): Properties {
    const properties = clone(input_properties);

    for (const field in properties) {
        //  Before formatting, append ref path to property definition
        const ref_path = `${ref_path_prefix}/${field}`;

        properties[field] = conditionallyAppendPropertyPath({
            schema: properties[field],
            ref_path,
        });

        //  Format value
        properties[field] = formatSchemaForAJV({ schema: properties[field], ref_path });

        //  @todo: Handle `.not` (https://json-schema.org/understanding-json-schema/reference/combining.html#not)
    }

    return properties;
}

/**
 * Format string schema for AJV compatibility
 * - Convert a RegExp `pattern` value to a string
 * - Convert date-time `format*` values to strings
 */
function formatStringTypeForAJV<T extends ISchemaCoreType>(input_schema: T): T {
    // .pattern
    if (
        input_schema.type === SchemaTypeName.string &&
        input_schema.format !== SchemaStringFormat.date_time &&
        input_schema.pattern
    ) {
        const schema = input_schema as ISchemaString;

        if (schema.pattern) {
            // @ts-expect-error: Type conversion
            schema.pattern = schema.pattern.source;
        }

        return schema as T;
    }

    // dates
    if (
        input_schema.type === SchemaTypeName.string &&
        input_schema.format === SchemaStringFormat.date_time
    ) {
        const schema = input_schema as ISchemaDate;

        const fields: Array<keyof ISchemaDate> = [
            'formatMinimum',
            'formatMaximum',
            'formatExclusiveMinimum',
            'formatExclusiveMaximum',
            'const',
        ];

        for (const k of fields) {
            if (schema[k]) {
                // @ts-expect-error: Type conversion
                schema[k] = dateToString(schema[k]);
            }
        }

        //  enum
        if (schema.enum) {
            // @ts-expect-error: Type conversion
            schema.enum = schema.enum.map(v => dateToString(v));
        }

        return schema as T;
    }

    return input_schema;
}

/**
 * Append a `AJV_EXTENSION_PROPERTY_PATH` value if it doesn't yet exist
 */
function conditionallyAppendPropertyPath<T extends ISchemaType>({
    schema,
    ref_path,
}: {
    schema: T;
    ref_path: string;
}): T {
    return {
        ...schema,
        [AJV_EXTENSION_PROPERTY_PATH]: schema[AJV_EXTENSION_PROPERTY_PATH] || ref_path,
    };
}
